import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePlatform } from "@/hooks/usePlatform";

interface SwipeBackOptions {
  /** Edge zone width in px from left edge that initiates the gesture. */
  edge?: number;
  /** Minimum horizontal distance to trigger back. */
  threshold?: number;
  /** Disable on certain pathnames (e.g., the root). */
  disabled?: boolean;
}

/**
 * useSwipeBack — iOS-style edge-swipe navigation back.
 *
 * Activates only on iOS / iPad form factors (Android uses system gesture).
 * Listens to pointer events at the document level; the gesture must start
 * within `edge` px of the left edge to count, mirroring UIKit's behaviour.
 * Triggers `history.back()` (works with React Router). Safe to mount once
 * at the app shell; tears down listeners on unmount or platform change.
 */
export function useSwipeBack({
  edge = 24,
  threshold = 80,
  disabled = false,
}: SwipeBackOptions = {}) {
  const { isIOS, isIPad } = usePlatform();
  const navigate = useNavigate();
  const stateRef = useRef<{ x: number; y: number; active: boolean } | null>(null);

  useEffect(() => {
    if (disabled) return;
    if (!isIOS && !isIPad) return;

    const onStart = (e: PointerEvent) => {
      if (e.clientX > edge) return;
      stateRef.current = { x: e.clientX, y: e.clientY, active: true };
    };
    const onEnd = (e: PointerEvent) => {
      const s = stateRef.current;
      stateRef.current = null;
      if (!s?.active) return;
      const dx = e.clientX - s.x;
      const dy = Math.abs(e.clientY - s.y);
      // Mostly-horizontal swipe past threshold → go back.
      if (dx > threshold && dy < 60) {
        navigate(-1);
      }
    };
    const onCancel = () => {
      stateRef.current = null;
    };

    document.addEventListener("pointerdown", onStart, { passive: true });
    document.addEventListener("pointerup", onEnd, { passive: true });
    document.addEventListener("pointercancel", onCancel, { passive: true });
    return () => {
      document.removeEventListener("pointerdown", onStart);
      document.removeEventListener("pointerup", onEnd);
      document.removeEventListener("pointercancel", onCancel);
    };
  }, [isIOS, isIPad, edge, threshold, disabled, navigate]);
}

export default useSwipeBack;
